import {
    ArrayUtil, CompanySettings, DOMUtil, GeneralSettings, getThemeColor, MenuItem, Navigation, ObjectUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ButtonVariant, ClearButtonVisible, Component, HorizontalSpacer, Image, ImageType, KeyHandler,
    Label, MainPageAccessor, Overlay, Panel, PanelProps, Textbox, TreeNode
} from "@mcleod/components";
import { MenuFavorites } from "./MenuFavorites";
import { MenuLauncher } from "./MenuLauncher";
import { MenuTreeNodeBranch } from "./MenuTreeNodeBranch";
import { MenuTreeNodeLeaf } from "./MenuTreeNodeLeaf";
import { PanelFavorites } from "./PanelFavorites";
import { PanelToolbox } from "./PanelToolbox";
import { MainMenuKeyHandler } from "./MainMenuKeyHandler";
import { MainMenuTree } from "./MainMenuTree";

export class MainMenu extends Panel {
    panelHeader: Panel;
    closeButton: Button;
    //used for the toolbox portion of the menu
    panelToolbox: PanelToolbox;
    //used for the favorites portion of the menu
    panelFavorites: PanelFavorites;
    //used for the regular menu (including the filter)
    textSearchMenu: Textbox;
    treeMenu: MainMenuTree;
    //other
    overlay: Overlay;
    menuLauncher: MenuLauncher;
    private menuKeyHandler: MainMenuKeyHandler;
    private _headerStarOffset: number;

    constructor(props?: Partial<PanelProps>) {
        super({
            backgroundColor: "subtle.lightest",
            color: "subtle.darker",
            width: 300,
            height: 0,
            padding: 0,
            ...props });
        const settings = GeneralSettings.get();
        if (settings == null || settings.menu == null)
            this.add(new Label({ text: "The menu hasn't been loaded.", color: "subtle.light" }));
        else {
            this.menuLauncher = new MenuLauncher(this);
            this.createFilteredMenu();
            this.add(this.createHeaderPanel())
            this.panelFavorites = new PanelFavorites(this, { id: "panelMenuFavorites", fillRow: true, scrollY: true });
            this.panelToolbox = PanelToolbox.create(this);
            if (this.panelToolbox != null)
                this.add(this.panelToolbox);
            this.add(this.panelFavorites);
            this.add(this.textSearchMenu);
            this.add(this.treeMenu);
            this.populateMenuTree();
            this.menuKeyHandler = new MainMenuKeyHandler(this);
        }
    }

    get treeFavoritesMenu(): MainMenuTree {
        return this.panelFavorites.treeFavoritesMenu;
    }

   hide() {
        this.unselectAllMenus();
        Overlay.hideOverlay(this.overlay);
    }

    private createHeaderPanel(): Panel {
        const result = new Panel({ id: "headerPanel", fillRow: true});
        const menuLabel = new Label({
            text: "Menu",
            allowSelect: false,
            fontSize: "xxxlarge",
            fontBold: true,
            color: "component.palette.menu.mainColor",
            rowBreak: false
        });

        this.closeButton = new Button({
            color: "component.palette.menu.secondaryColor",
            imageName: ImageName.close,
            imageHeight: 28,
            imageWidth: 28,
            variant: ButtonVariant.round,
            onClick: () => this.hide()
        });
        result.add(menuLabel, new HorizontalSpacer(), this.closeButton);
        return result;
    }

    getHeaderHomeComponent(): Component {
        let result: Component = null;
        const logo = CompanySettings.get()?.header_logo;
        if (logo != null) {
            result = new Image({
                id: "imageLogo",
                imageType: ImageType.IMG,
                imageBytes: logo,
                height: 30,
                width: "auto",
            });
        } else {
            result = new Button({
                imageName: ImageName.home,
                imageHeight: 24,
                imageWidth: 24,
                variant: ButtonVariant.round,
                color: "primary.darker",
                tooltip: "Return to your home page",
            });
        }
        result.addClickListener(() => {
            Navigation.navigateTo("");
            this.hide();
        });
        return result;
    }

    private createFilteredMenu() {
        this.textSearchMenu = new Textbox({
            placeholder: "Search",
            color: "background2-reverse",
            captionVisible: false,
            clearButtonVisible: ClearButtonVisible.WHEN_FOCUSED,
            fillRow: true,
            margin: 4
        });
        this.textSearchMenu._inputDiv.style.backgroundColor = getThemeColor("primary.reverse");
        this.textSearchMenu.imagePre = new Image({
            name: ImageName.search,
            color: "strokePrimary",
            paddingLeft: 4,
            height: 24,
            width: 24
        });

        this.textSearchMenu.addFocusListener(() => this.unselectMenus(this.treeFavoritesMenu));
        this.textSearchMenu.addChangeListener(() => this.populateMenuTree());


        this.treeMenu = new MainMenuTree(this, {
            id: "mainMenuTree",
            fillHeight: true,
            paddingLeft: 0
        });
    }

    clearSearch() {
        if (this.textSearchMenu != null) {
            this.textSearchMenu.text = "";
        }
    }

    show(anchor: Component) {
        this.setProps({ style: { transition: "" }, height: 0, padding: 0 });
        this.clearSearch();
        Overlay.alignToAnchor(this, anchor); // really just to get the CSS since we are positioning manually
        this.setProps({ left: 0, top: MainPageAccessor.pageHeaderHeight, style: { transition: "height 250ms, opacity 500ms" } })
        this.overlay = Overlay.showInOverlay(this);
        this.unselectAllMenus();
        this.focus();
        setTimeout(() => this.setProps({ height: `calc(100vh - ${MainPageAccessor.pageHeaderHeight}px)`, padding: 8, borderWidth: 0 }), 0);
    }

    override focus() {
        if (this.textSearchMenu != null)
            this.textSearchMenu.focus();
    }

    private populateMenuTree() {
        const filter = this.textSearchMenu.text.toLowerCase();
        const root = new TreeNode({ tree: this.treeMenu });
        let filtered = GeneralSettings.get().menu.items;
        if (filter?.length > 0) {
            filtered = ObjectUtil.deepCopy(filtered);
            filtered = ObjectUtil.filterRecursiveArrayContainingString<MenuItem>(filtered, "items", "caption", filter);
        }

        for (const item of filtered) {
            if (ArrayUtil.isEmptyArray(item.items)) {
                root.addChild(new MenuTreeNodeLeaf(this, { menuItem: item }));
            } else {
                root.addChild(new MenuTreeNodeBranch(this, { menuItem: item }));
            }
        }
        if (filter.length > 0)
            root.expandAll();

        this.treeMenu.getRootNode().setChildren(root.getChildren());

        // Auto-select the first leaf node if all leaf nodes have the same caption and path.
        // We need to use a map because duplicate leafs can exist in multiple branches.
        const leafMap: Map<string, MenuTreeNodeLeaf> = new Map();
        root.recurseChildren(node => {
            if (node instanceof MenuTreeNodeLeaf) {
                const { caption, path } = node.menuItem;
                const key = JSON.stringify({ caption, path });
                if (!leafMap.has(key)) {
                    leafMap.set(key, node);
                }
            }
        });

        if (leafMap.size === 1) {
            requestAnimationFrame(() => {
                this.treeMenu.selectedNode = Array.from(leafMap.values())[0];
                this.textSearchMenu.focus();
            });
        }
    }

    selectFirstToolboxItem() {
        this.panelToolbox?.focusFirstToolboxItem();
    }

    selectLastToolboxItem() {
        this.panelToolbox?.focusLastToolboxItem();
    }

    selectFirstFavorite() {
        if (this.treeFavoritesMenu.getRootNode().getChildCount() > 0) {
            this.treeFavoritesMenu.getRootNode().selectFirstChild();
        } else {
            this.focus();
        }
    }

    override getKeyHandlers(): KeyHandler[] {
        let result = [];
        if (this.keyHandlers != null)
            result = [...this.keyHandlers];
        result.push({
            key: "ALL",
            listener: (event) => this.menuKeyHandler?.handleSharedKeys(event),
            element: null,
            scope: this._element
        });
        return result;
    }

    private getNumToolboxItems(): number {
        return ArrayUtil.getLength(GeneralSettings.get().menu_toolbox_items);
    }

    private convertHotKeyStringToNumber(zeroThroughNineKey: string): number {
        if (zeroThroughNineKey < "0" || zeroThroughNineKey > "9")
            return -1;
        return zeroThroughNineKey !== "0" ? parseInt(zeroThroughNineKey) : 10;
    }

    showToolbox(event: KeyboardEvent): boolean {
        const keyAsNumber = this.convertHotKeyStringToNumber(event.key);
        if (keyAsNumber >= 1 && keyAsNumber <= this.getNumToolboxItems()) {
            const item = GeneralSettings.get().menu_toolbox_items[keyAsNumber - 1] as MenuItem;
            if (item.caption === "MPact")
                this.menuLauncher.openMpactInBrowser();
            else if (item.path.startsWith("https"))
                window.open(item.path, "", "popup");
            else
                this.menuLauncher.openPageInSlideUp(item);
            return true;
        }
        return false;
    }

    showFavorite(event: KeyboardEvent, keyValue: string): boolean {
        const keyAsNumber = this.convertHotKeyStringToNumber(keyValue);
        if (keyAsNumber >= 1 && keyAsNumber <= MenuFavorites.getFavoriteCount()) {
            const item = MenuFavorites.getFavorites()[keyAsNumber - 1];
            this.menuLauncher.openPage(item.data, event.ctrlKey);
            return true;
        }
        return false;
    }

    unselectAllMenus() {
        this.unselectMenus(this.treeMenu, this.treeFavoritesMenu);
    }

    unselectMenus(...menus: MainMenuTree[]) {
        for (const menu of menus) {
            if (menu != null) {
                menu._element.blur();
                menu.selectedNode = null;
            }
        }
    }

    static findMenuItemForPath(searchPath: string, searchMenuItem: MenuItem = GeneralSettings.get().menu): MenuItem {
        if (searchMenuItem.path === searchPath)
            return searchMenuItem;
        if (searchMenuItem.items != null) {
            for (const item of searchMenuItem.items) {
                const sub = this.findMenuItemForPath(searchPath, item);
                if (sub != null)
                    return sub;
            }
        }
        return undefined;
    }

    get headerStarOffset(): number {
        if (this._headerStarOffset != null) {
            return this._headerStarOffset;
        }
        const headerStar = this.panelFavorites?.imageFavorite?._element;
        if (headerStar == null) {
            this._headerStarOffset = 8;
            return this._headerStarOffset;
        }
        const rightValue = DOMUtil.getComputedStyleAsNumber("right", headerStar);
        const marginRightValue = DOMUtil.getComputedStyleAsNumber("marginRight", headerStar);
        this._headerStarOffset = rightValue || marginRightValue || 8;
        return this._headerStarOffset;
    }
}
